/**
 * Every route's document metadata, in one table.
 *
 * This replaces Next's `metadata` exports, its title `template`, and the
 * robots.ts / sitemap.ts metadata routes. Three consumers read it and they must
 * never disagree:
 *
 *   1. scripts/prerender.ts  — bakes the tags into each emitted HTML file, so
 *      OG scrapers (which do not run JS) see them.
 *   2. useDocumentHead()     — upserts the same tags on client-side navigation
 *      and in dev, where there is no prerender pass.
 *   3. buildSitemap()        — below.
 *
 * Keeping them in one table is the whole point: the previous setup spread the
 * same title across a layout, a page and an OG block, which is how a site ends
 * up sharing one headline and displaying another.
 */

import { SITE_URL } from './site';
import { SERVICES_ENABLED, services } from './services';

/* One definition each. These are quoted by the page title, Open Graph and
   Twitter, so three literals meant three places to miss when the city or the
   positioning changed. */
export const SITE_TITLE =
  'Butter Websites - Managed Ecommerce Websites | Cincinnati, Ohio';
export const SITE_DESCRIPTION =
  'Butter Websites builds, launches, and manages Shopify stores for growing businesses.';
export const SITE_NAME = 'Butter Websites';

/* 1200x630. Declared explicitly because Next's opengraph-image file convention
   used to supply the dimensions, and LinkedIn in particular will skip an image
   it cannot size without downloading first. */
export const OG_IMAGE = '/opengraph-image.png';
export const OG_IMAGE_WIDTH = '1200';
export const OG_IMAGE_HEIGHT = '630';

/** Next applied `template: '%s | Butter Websites'` to bare child titles. */
export function branded(title: string): string {
  return `${title} | ${SITE_NAME}`;
}

/* The two document shells. There was no app/layout.tsx: (site) and (play) were
   parallel root layouts with different <html>/<body> classes, and crossing
   between them was a hard navigation. Emitting a separate HTML file per route
   preserves that exactly -- see scripts/prerender.ts. */
const SITE_HTML_CLASS = 'bg-white text-ink-700 scroll-smooth';
const SITE_BODY_CLASS = 'font-body text-[17px] leading-[1.6]';

const PLAY_HTML_CLASS = 'bg-bw-ink text-white';
/* overscroll-none is not cosmetic: without it a tap near the top of the screen
   triggers Android Chrome's pull-to-refresh mid-game. */
const PLAY_BODY_CLASS =
  'font-body h-[100dvh] select-none overflow-hidden overscroll-none bg-bw-ink';

const DEFAULT_VIEWPORT = 'width=device-width, initial-scale=1';
/* The game paints edge to edge and pads its HUD with the safe-area insets
   itself, so let it under the notch. Pinch-zooming a fixed-viewport game only
   ever strands the player at an odd scale mid-run; the page has no text to
   enlarge. */
const PLAY_VIEWPORT =
  'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover';

export type ChangeFrequency = 'weekly' | 'monthly' | 'yearly';

export interface RouteMeta {
  /** Path as served, always with a leading slash and no trailing slash. */
  path: string;
  /** Full <title>, brand suffix included. */
  title: string;
  description: string;
  /** OG/Twitter title. Never run through the brand template, so it spells the
   *  branded form out where it wants one. */
  ogTitle: string;
  ogDescription: string;
  htmlClass: string;
  bodyClass: string;
  viewport: string;
  themeColor?: string;
  /** null keeps the route out of the sitemap. */
  sitemap: { changeFrequency: ChangeFrequency; priority: number } | null;
  /**
   * Emit the document shell and head tags, but an empty <div id="root">.
   *
   * For a route behind React.lazy this is not an optimisation, it is the fix:
   * renderToString cannot await a lazy chunk, so it emits the Suspense fallback
   * and marks the boundary unresolved. Hydrating that throws React error #419,
   * discards the server subtree and re-renders on the client anyway. Shipping
   * an empty root instead makes main.tsx take its createRoot branch, which does
   * the same work without the error -- and costs nothing, because the markup
   * being skipped is a <canvas> the client has to paint from scratch regardless.
   */
  clientOnly?: boolean;
}

function siteRoute(
  route: Omit<RouteMeta, 'htmlClass' | 'bodyClass' | 'viewport'>,
): RouteMeta {
  return {
    ...route,
    htmlClass: SITE_HTML_CLASS,
    bodyClass: SITE_BODY_CLASS,
    viewport: DEFAULT_VIEWPORT,
  };
}

const SERVICES_INDEX_DESCRIPTION =
  'Shopify development, POS, store maintenance, SEO and email marketing for independent boutiques and artisan food brands.';

const PLAY_DESCRIPTION =
  'Stack pats of butter as high as you can. Miss and the pat gets cut down to size. A tiny arcade game by Butter Websites.';
/* Deliberately the shorter form: the share card truncates, and the last
   sentence is the one that reads as filler once clipped. */
const PLAY_OG_DESCRIPTION =
  'Stack pats of butter as high as you can. Miss and the pat gets cut down to size.';

export const HOME_ROUTE: RouteMeta = siteRoute({
  path: '/',
  title: SITE_TITLE,
  description: SITE_DESCRIPTION,
  ogTitle: SITE_TITLE,
  ogDescription: SITE_DESCRIPTION,
  sitemap: { changeFrequency: 'weekly', priority: 1 },
});

export const SERVICES_INDEX_ROUTE: RouteMeta = siteRoute({
  path: '/services',
  title: branded('Shopify Services'),
  description: SERVICES_INDEX_DESCRIPTION,
  ogTitle: branded('Shopify Services'),
  ogDescription: SERVICES_INDEX_DESCRIPTION,
  sitemap: { changeFrequency: 'monthly', priority: 0.8 },
});

/* The arcade page. Listed so it can be shared and indexed, at a low priority
   because it sells nothing. */
export const PLAY_STACK_ROUTE: RouteMeta = {
  path: '/play/stack',
  title: branded('Butter Stack - a game'),
  description: PLAY_DESCRIPTION,
  ogTitle: 'Butter Stack - a game',
  ogDescription: PLAY_OG_DESCRIPTION,
  htmlClass: PLAY_HTML_CLASS,
  bodyClass: PLAY_BODY_CLASS,
  viewport: PLAY_VIEWPORT,
  themeColor: '#000000',
  sitemap: { changeFrequency: 'yearly', priority: 0.3 },
  clientOnly: true,
};

export const NOT_FOUND_ROUTE: RouteMeta = siteRoute({
  path: '/404',
  title: branded('Page not found'),
  description: SITE_DESCRIPTION,
  ogTitle: branded('Page not found'),
  ogDescription: SITE_DESCRIPTION,
  sitemap: null,
});

export function serviceRoute(slug: string): RouteMeta | null {
  const service = services.find((s) => s.slug === slug);
  if (!service) return null;

  /* metaTitle carries no brand suffix -- it was written for Next's title
     template, so apply the same suffix here. */
  const title = branded(service.metaTitle);
  return siteRoute({
    path: `/services/${service.slug}`,
    title,
    description: service.metaDescription,
    ogTitle: title,
    ogDescription: service.metaDescription,
    sitemap: { changeFrequency: 'monthly', priority: 0.7 },
  });
}

/**
 * Every route to prerender.
 *
 * The services entries are omitted entirely while SERVICES_ENABLED is false,
 * which is what generateStaticParams returning [] used to achieve: the routes
 * still exist in the router and render the 404 page, they just are not built.
 */
export function prerenderRoutes(): RouteMeta[] {
  const routes: RouteMeta[] = [HOME_ROUTE];

  if (SERVICES_ENABLED) {
    routes.push(SERVICES_INDEX_ROUTE);
    for (const service of services) {
      const route = serviceRoute(service.slug);
      if (route) routes.push(route);
    }
  }

  routes.push(PLAY_STACK_ROUTE, NOT_FOUND_ROUTE);
  return routes;
}

/**
 * sitemap.xml. Routes with `sitemap: null` are omitted -- listing URLs that
 * answer 404 is worse than not listing them.
 */
export function buildSitemap(now: Date = new Date()): string {
  const lastModified = now.toISOString();
  const entries = prerenderRoutes()
    .filter((route) => route.sitemap !== null)
    .map((route) => {
      const { changeFrequency, priority } = route.sitemap!;
      /* SITE_URL has no trailing slash, so the home path would otherwise
         render as "https://www.butterwebsites.com/" with a bare slash that
         does not match the canonical emitted on the page itself. */
      const loc = route.path === '/' ? SITE_URL : `${SITE_URL}${route.path}`;
      return [
        '  <url>',
        `    <loc>${loc}</loc>`,
        `    <lastmod>${lastModified}</lastmod>`,
        `    <changefreq>${changeFrequency}</changefreq>`,
        `    <priority>${priority}</priority>`,
        '  </url>',
      ].join('\n');
    });

  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">',
    ...entries,
    '</urlset>',
    '',
  ].join('\n');
}

export function buildRobots(): string {
  return [
    'User-Agent: *',
    'Allow: /',
    '',
    `Sitemap: ${SITE_URL}/sitemap.xml`,
    '',
  ].join('\n');
}
